/**
 * Pure reducer: a workflow run's operation events → live per-step progress.
 * Event types come from backend/src/schemas/stream_events.py (flow events, MHS-1010).
 */
import type { StreamEvent } from "@/api";

export interface FlowStepProgress {
  stepNumber: number;
  agentName?: string;
  status: "running" | "done" | "failed" | "paused";
  /** Streamed text so far, replaced by the step's final output when it ends. */
  output?: string;
  error?: string;
}

export interface FlowRunStreamState {
  /** The run this operation is executing, from flow_start. */
  runUuid?: string;
  flowName?: string;
  totalSteps: number;
  steps: FlowStepProgress[];
  status: "starting" | "running" | "completed" | "failed" | "paused";
  error?: string;
  finalOutput?: string;
}

export const initialFlowRunStream: FlowRunStreamState = {
  totalSteps: 0,
  steps: [],
  status: "starting",
};

function upsert(steps: FlowStepProgress[], patch: Partial<FlowStepProgress> & { stepNumber: number }) {
  const next = [...steps];
  const i = next.findIndex((s) => s.stepNumber === patch.stepNumber);
  if (i >= 0) next[i] = { ...next[i], ...patch };
  else next.push({ status: "running", ...patch });
  return next;
}

export function flowRunStreamReducer(state: FlowRunStreamState, event: StreamEvent): FlowRunStreamState {
  switch (event.event) {
    case "flow_start":
      return {
        ...state,
        runUuid: event.data.run_uuid ?? state.runUuid,
        flowName: event.data.flow_name ?? undefined,
        totalSteps: event.data.total_steps ?? 0,
        status: "running",
      };
    case "step_start":
      return {
        ...state,
        status: "running",
        steps: upsert(state.steps, {
          stepNumber: event.data.step_number ?? 0,
          agentName: event.data.agent_name ?? undefined,
          status: "running",
        }),
      };
    case "step_content": {
      const stepNumber = event.data.step_number ?? 0;
      const prior = state.steps.find((s) => s.stepNumber === stepNumber)?.output ?? "";
      return { ...state, steps: upsert(state.steps, { stepNumber, output: prior + event.data.content }) };
    }
    case "step_end": {
      const stepNumber = event.data.step_number ?? 0;
      // Some paths cut step_end's output to 500 characters: keep the full streamed text when there is one.
      const streamed = state.steps.find((s) => s.stepNumber === stepNumber)?.output;
      return {
        ...state,
        steps: upsert(state.steps, {
          stepNumber,
          status: event.data.status === "failed" ? "failed" : "done",
          output: streamed || event.data.output || undefined,
        }),
      };
    }
    case "step_failed":
      return {
        ...state,
        steps: upsert(state.steps, {
          stepNumber: event.data.step_number ?? 0,
          status: "failed",
          error: event.data.error ?? undefined,
        }),
      };
    case "step_paused":
      return {
        ...state,
        runUuid: event.data.run_uuid ?? state.runUuid,
        status: "paused",
        steps: upsert(state.steps, { stepNumber: event.data.step_number ?? 0, status: "paused" }),
      };
    case "flow_end":
      return { ...state, status: "completed", finalOutput: event.data.output ?? undefined };
    case "flow_failed":
      return { ...state, status: "failed", error: event.data.error ?? undefined };
    case "operation_failed":
      // Fails before flow_start too (budget, no steps): there's no run to open.
      return state.status === "failed" ? state : { ...state, status: "failed", error: event.data.error };
    default:
      return state;
  }
}
